'use client';

import { createContext, useContext, type ComponentType, type SVGProps } from 'react';
import { Tip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Any SVG icon component usable in the rail — covers both `lucide-react`
 * icons and our own glyphs (e.g. the canonical agent `BotIcon`), which are
 * plain `forwardRef` SVG components rather than `LucideIcon`s.
 */
export type RailIcon = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Rail layout context. The rail renders one of three item variants:
 *   - `compact` — the desktop default: a centered icon with a small label
 *     BELOW it (~64px rail). This is the signed-off wireframe look.
 *   - `wide` — a full-width icon + label ROW (the mobile drawer).
 *   - `icons` — a centered icon puck with a tooltip (no label).
 * Defaults to `icons` so un-provided usages stay label-free.
 */
export type RailVariant = 'icons' | 'compact' | 'wide';
export const RailLayoutContext = createContext<{ variant: RailVariant }>({
  variant: 'icons',
});
export function useRailLayout() {
  return useContext(RailLayoutContext);
}

/**
 * Shared button surface for every rail control (panel views, Create,
 * Tools, Zoom). Visual language per §0 of the rail-redesign doc: no new
 * glass here (the glass lives on the rail container); items are flat icon
 * targets with a filled active pill.
 */
export function railButtonClass(active: boolean): string {
  return cn(
    'relative inline-flex h-9 w-9 items-center justify-center rounded-xl',
    'transition-colors select-none outline-none',
    'focus-visible:ring-2 focus-visible:ring-primary/50',
    active
      ? 'bg-primary/85 text-primary-foreground'
      : 'text-foreground/70 hover:text-foreground hover:bg-accent/60',
  );
}

export function RailItem({
  icon: Icon,
  label,
  active = false,
  onClick,
  badge,
  testId,
  onboardingAnchor,
  hideLabel = false,
  disabled = false,
  hint,
  keycap,
}: {
  icon: RailIcon;
  label: string;
  active?: boolean;
  onClick: () => void;
  /** Render inert + dimmed (e.g. while a widget is fullscreen). */
  disabled?: boolean;
  /** Optional numeric badge (e.g. unread notifications). */
  badge?: number;
  /**
   * Optional keyboard-shortcut hint (e.g. "⌘⇧1"). Surfaced in the
   * hover tooltip only — never in the always-visible compact label or
   * the mobile drawer rows, where it would just be noise.
   */
  hint?: string;
  /**
   * Transient keycap badge (e.g. "1", "K") shown in the item's bottom-right
   * corner while the user HOLDS the chord modifiers (⌘⇧) — a just-in-time
   * reveal of which key completes the chord. Rendered on the desktop
   * variants only; the mobile drawer has no keyboard.
   */
  keycap?: string;
  /** `data-testid` for e2e targeting. */
  testId?: string;
  /** `data-onboarding-anchor` so the onboarding overlay can pin to it. */
  onboardingAnchor?: string;
  /**
   * In the desktop `compact` variant, render icon-only (no text label)
   * while keeping the subtle compact active pill + tooltip. Used by the
   * bottom-cluster items whose labels ("Collaborate" / "Notifications")
   * are too long for the 68px rail and were truncating + squeezing the
   * account avatar. `wide` (mobile drawer) ignores this — labels fit.
   */
  hideLabel?: boolean;
}) {
  const { variant } = useRailLayout();
  const hasBadge = badge != null && badge > 0;
  const tipLabel = hint ? `${label} · ${hint}` : label;
  const keycapChip = keycap ? (
    <span
      aria-hidden
      className="pointer-events-none absolute bottom-0.5 right-1 z-10 inline-flex h-4 min-w-4 items-center justify-center rounded-[5px] border border-border/60 bg-background/95 px-0.5 font-mono text-[10px] font-medium leading-none text-foreground/80 shadow-sm"
    >
      {keycap}
    </span>
  ) : null;

  // Compact (labeled, vertical) variant — the desktop default: a centered icon
  // with a small label below it, and a subtle filled pill when active.
  if (variant === 'compact') {
    const button = (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={active}
        aria-current={active ? 'page' : undefined}
        data-testid={testId}
        data-onboarding-anchor={onboardingAnchor}
        className={cn(
          'group relative flex w-full flex-col items-center gap-1 rounded-xl px-1 text-[11px] leading-none',
          hideLabel ? 'py-1' : 'py-1.5',
          'transition-colors select-none outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          disabled && 'pointer-events-none opacity-35',
          active ? 'text-foreground' : 'text-foreground/60 hover:text-foreground',
        )}
      >
        {/* The BUTTON stays full width (a generous hit target), but the
            highlight is a square pad around the glyph. A pill spanning the
            whole rail around a 19px icon read as a different system from the
            app pucks below; at 36px it shares their column rhythm. */}
        <span
          className={cn(
            'relative inline-flex h-9 w-9 items-center justify-center rounded-xl transition-colors',
            active ? 'bg-foreground/[0.08]' : 'group-hover:bg-accent/60',
          )}
        >
          <Icon className="h-[19px] w-[19px]" strokeWidth={1.6} />
          {hasBadge ? (
            <span
              aria-hidden
              className="absolute -top-1.5 -right-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white"
            >
              {badge > 99 ? '99+' : badge}
            </span>
          ) : null}
        </span>
        {hideLabel ? null : <span className="max-w-full truncate pb-px leading-[1.35]">{label}</span>}
        {keycapChip}
      </button>
    );
    // No visible label → surface it as a tooltip instead. A visible
    // label with a shortcut hint still gets a tooltip so the chord is
    // discoverable on hover.
    return hideLabel || hint ? (
      <Tip label={tipLabel} side="right">
        {button}
      </Tip>
    ) : (
      button
    );
  }

  // Wide (labeled) variant — a full-width icon + label row.
  if (variant === 'wide') {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={active}
        aria-current={active ? 'page' : undefined}
        data-testid={testId}
        data-onboarding-anchor={onboardingAnchor}
        className={cn(
          'flex h-9 w-full items-center gap-3 rounded-xl px-2.5 text-left text-sm',
          'transition-colors select-none outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          disabled && 'pointer-events-none opacity-35',
          active
            ? 'bg-primary/85 text-primary-foreground'
            : 'text-foreground/80 hover:text-foreground hover:bg-accent/60',
        )}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.6} />
        <span className="flex-1 truncate">{label}</span>
        {hasBadge ? (
          <span
            aria-hidden
            className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white"
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </button>
    );
  }

  // Collapsed (icon) variant — centered puck with a tooltip.
  return (
    <Tip label={tipLabel} side="right">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={active}
        aria-current={active ? 'page' : undefined}
        data-testid={testId}
        data-onboarding-anchor={onboardingAnchor}
        className={cn(railButtonClass(active), disabled && 'pointer-events-none opacity-35')}
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
        {hasBadge ? (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white"
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
        {keycapChip}
      </button>
    </Tip>
  );
}
